import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { StudentProgressApi } from '../services/api/StudentProgressApi';
import { StudyGuideApi } from '../services/api/StudyGuideApi';
import TopicDetailModal from '../components/study-guide/TopicDetailModal';
import PdfExportButton from '../components/analytics/PdfExportButton';
import { ReportGenerator } from '../utils/ReportGenerator';
import {
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, AreaChart, Area, Legend
} from 'recharts';

const COLORS = ['#007bff', '#28a745', '#ffc107', '#dc3545', '#17a2b8', '#6f42c1', '#20c997', '#fd7e14'];

/**
 * StudentProgress — User-facing dashboard showing the current student's own learning data.
 * Not admin-restricted; any authenticated user can view their own progress.
 * Wires: StudentProgressApi.getMyProfile(), getMyActivity(), getMySessions()
 */
export default function StudentProgress() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [activity, setActivity] = useState(null);
  const [sessions, setSessions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [days, setDays] = useState(30);

  // Study Guide state
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [studyGuide, setStudyGuide] = useState(null);
  const [guideLoading, setGuideLoading] = useState(false);
  const [refreshingGuide, setRefreshingGuide] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [generatingSummary, setGeneratingSummary] = useState(false);

  // Load courses on mount
  useEffect(() => {
    StudyGuideApi.getCourses()
      .then(setCourses)
      .catch(err => console.warn('Failed to load courses:', err));
  }, []);

  // Load study guide when course selection changes
  useEffect(() => {
    setGuideLoading(true);
    StudyGuideApi.getStudyGuide(selectedCourse)
      .then(setStudyGuide)
      .catch(err => console.warn('Failed to load study guide:', err))
      .finally(() => setGuideLoading(false));
  }, [selectedCourse]);

  const handleRefreshGuide = useCallback(async () => {
    setRefreshingGuide(true);
    try {
      const guide = await StudyGuideApi.refreshRecommendations(selectedCourse);
      setStudyGuide(guide);
    } catch (err) {
      console.error('Failed to refresh recommendations:', err);
    } finally {
      setRefreshingGuide(false);
    }
  }, [selectedCourse]);

  const handleGenerateTutorSummary = useCallback(async () => {
    if (!studyGuide) return;
    setGeneratingSummary(true);
    try {
      const summaryRes = await StudyGuideApi.generateTutorSummary({
        mastery_data: (studyGuide.topicMastery || []).map(m => ({
          concept: m.conceptSlug,
          score: m.masteryScore,
          confidence: m.confidence,
          evidence_count: m.evidenceCount
        })),
        recommendations: (studyGuide.recommendations || []).map(r => ({
          type: r.recommendationType,
          title: r.title,
          priority: r.priority
        })),
        deadlines: (studyGuide.upcomingDeadlines || []).map(d => ({
          title: d.title,
          days_until: d.daysUntil,
          urgent: d.urgent
        })),
        recent_activity: {}
      });
      // Save summary to DB
      const contextHash = JSON.stringify(studyGuide.topicMastery?.length || 0).substring(0, 32);
      await StudyGuideApi.saveTutorSummary({
        endeavorId: selectedCourse,
        summaryText: summaryRes.summary,
        contextHash
      });
      // Update local state
      setStudyGuide(prev => ({
        ...prev,
        tutorSummary: { summaryText: summaryRes.summary, generatedAt: new Date().toISOString() }
      }));
    } catch (err) {
      console.error('Failed to generate tutor summary:', err);
    } finally {
      setGeneratingSummary(false);
    }
  }, [studyGuide, selectedCourse]);

  const handleCompleteRec = useCallback(async (recId) => {
    try {
      await StudyGuideApi.completeRecommendation(recId);
      setStudyGuide(prev => ({
        ...prev,
        recommendations: prev.recommendations.filter(r => r.id !== recId)
      }));
    } catch (err) {
      console.error('Failed to complete recommendation:', err);
    }
  }, []);

  const handleDismissRec = useCallback(async (recId) => {
    try {
      await StudyGuideApi.dismissRecommendation(recId);
      setStudyGuide(prev => ({
        ...prev,
        recommendations: prev.recommendations.filter(r => r.id !== recId)
      }));
    } catch (err) {
      console.error('Failed to dismiss recommendation:', err);
    }
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const now = new Date();
      const from = new Date(now.getTime() - days * 86400000).toISOString();
      const to = now.toISOString();

      const [profileRes, activityRes, sessionsRes] = await Promise.all([
        StudentProgressApi.getMyProfile(),
        StudentProgressApi.getMyActivity(days),
        StudentProgressApi.getMySessions({ from, to, page: 0, pageSize: 10 })
      ]);

      setProfile(profileRes);
      setActivity(activityRes);
      setSessions(sessionsRes);
    } catch (err) {
      console.error('Failed to load student progress:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { loadData(); }, [loadData]);

  const hasProfile = profile && Object.keys(profile).length > 0;
  const contentRef = useRef(null);

  const handlePdfExport = useCallback(async () => {
    const now = new Date();
    const from = new Date(now.getTime() - days * 86400000).toISOString();
    const report = new ReportGenerator('Student Learning Progress', { from, to: now.toISOString() });

    if (profile) {
      const fmtTime = (s) => { const h = Math.floor(s/3600); const m = Math.floor((s%3600)/60); return h > 0 ? `${h}h ${m}m` : `${m}m`; };
      report.addMetrics([
        { label: 'Study Time', value: fmtTime(profile.totalStudyTimeSeconds || 0), sublabel: `${profile.totalSessions || 0} sessions` },
        { label: 'Documents', value: String(profile.documentsProcessed || 0) },
        { label: 'Quizzes Done', value: String(profile.totalQuizzesCompleted || 0), sublabel: profile.avgQuizScore != null ? `Avg: ${Number(profile.avgQuizScore).toFixed(0)}%` : '' },
        { label: 'AI Prompts', value: String(profile.totalAiPrompts || 0) },
      ]);
    }

    if (contentRef.current) {
      report.addSectionHeading('Activity Overview');
      await report.addChartFromRef(contentRef, 'Daily activity and event breakdown');
    }

    if (sessions?.sessions?.length) {
      report.addSectionHeading('Recent Sessions');
      report.addTable(
        ['Date', 'Duration', 'Events', 'Docs', 'Chats', 'Quizzes', 'Quiz Score'],
        sessions.sessions.map(s => [
          s.startedAt ? new Date(s.startedAt).toLocaleDateString() : '—',
          s.durationSeconds ? `${Math.floor(s.durationSeconds / 60)}m` : '—',
          s.eventCount || 0, s.documentsOpened || 0, s.chatPromptsSent || 0,
          s.quizzesCompleted || 0, s.avgQuizScore != null ? `${Number(s.avgQuizScore).toFixed(0)}%` : '—'
        ])
      );
    }

    if (activity?.eventBreakdown?.length) {
      report.addSectionHeading('Event Breakdown');
      report.addTable(
        ['Event Type', 'Count', 'Last Occurred'],
        activity.eventBreakdown.map(e => [
          (e.eventType || '').replace(/_/g, ' '), e.count || 0,
          e.lastOccurred ? new Date(e.lastOccurred).toLocaleDateString() : '—'
        ])
      );
    }

    report.download('my-learning-progress');
  }, [profile, activity, sessions, days]);

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div style={{ fontSize: '2rem', marginBottom: '.5rem' }}>📊</div>
        <p style={{ color: 'var(--text-muted)' }}>Loading your progress...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div style={{ fontSize: '2rem', marginBottom: '.5rem' }}>⚠️</div>
        <p style={{ color: '#dc3545' }}>{error}</p>
        <button onClick={loadData} style={btnStyle}>Retry</button>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '1.5rem' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
        <div>
          <h2 style={{ margin: '0 0 .25rem 0' }}>📊 My Learning Progress</h2>
          <p style={{ fontSize: '.85rem', color: 'var(--text-muted)', margin: 0 }}>
            Welcome back, {user?.name || user?.email || 'Student'}! Here's how your learning journey is going.
          </p>
        </div>
        {hasProfile && <PdfExportButton onGenerate={handlePdfExport} label="Export Report" />}
      </div>

      {/* Time Range Selector */}
      <div style={{ display: 'flex', gap: '.5rem', marginBottom: '1.5rem' }}>
        {[7, 14, 30, 90].map(d => (
          <button
            key={d}
            onClick={() => setDays(d)}
            style={{
              padding: '.4rem .8rem',
              background: days === d ? '#007bff' : 'transparent',
              color: days === d ? '#fff' : 'inherit',
              border: days === d ? 'none' : '1px solid var(--border-light)',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '.85rem',
              fontWeight: days === d ? 600 : 400
            }}
          >
            {d}d
          </button>
        ))}
      </div>

      {!hasProfile ? (
        <div style={{ padding: '3rem', textAlign: 'center', background: 'var(--bg-card)', borderRadius: '12px', border: '1px solid var(--border-light)' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🎓</div>
          <h3 style={{ margin: '0 0 .5rem 0' }}>Start Your Learning Journey!</h3>
          <p style={{ color: 'var(--text-muted)', maxWidth: '400px', margin: '0 auto' }}>
            Upload a document, take a quiz, or start a chat to begin tracking your progress.
          </p>
        </div>
      ) : (
        <>
          {/* ═══ Study Guide Sections ═══ */}

          {/* Course Picker */}
          <CoursePicker
            courses={courses}
            selectedCourse={selectedCourse}
            onSelect={setSelectedCourse}
          />

          {/* Tutor Summary Card */}
          <TutorSummaryCard
            summary={studyGuide?.tutorSummary}
            loading={guideLoading}
            generating={generatingSummary}
            onGenerate={handleGenerateTutorSummary}
          />

          {/* Next Steps / Recommendations */}
          <NextSteps
            recommendations={studyGuide?.recommendations || []}
            loading={guideLoading}
            refreshing={refreshingGuide}
            onRefresh={handleRefreshGuide}
            onComplete={handleCompleteRec}
            onDismiss={handleDismissRec}
          />

          {/* Upcoming Deadlines */}
          <UpcomingDeadlines deadlines={studyGuide?.upcomingDeadlines || []} />

          {/* Topic Mastery */}
          <TopicMastery
            mastery={studyGuide?.topicMastery || []}
            totalConcepts={studyGuide?.totalConcepts || 0}
            loading={guideLoading}
            onTopicClick={setSelectedTopic}
          />

          {/* ═══ Existing Analytics Sections ═══ */}

          {/* Profile Overview Cards */}
          <ProfileOverview profile={profile} />

          {/* Chart area — captured for PDF export */}
          <div ref={contentRef}>
            {/* Daily Activity Chart */}
            <DailyActivityChart data={activity?.dailyActivity} days={days} />

            {/* Event Breakdown */}
            <EventBreakdownChart data={activity?.eventBreakdown} />
          </div>

          {/* Recent Sessions */}
          <RecentSessions sessions={sessions} />
        </>
      )}

      {/* Topic Detail Modal */}
      {selectedTopic && (
        <TopicDetailModal
          slug={selectedTopic}
          endeavorId={selectedCourse}
          onClose={() => setSelectedTopic(null)}
        />
      )}
    </div>
  );
}

// ─── Course Picker ──────────────────────────────────────────────────

function CoursePicker({ courses, selectedCourse, onSelect }) {
  if (!courses || courses.length === 0) return null;

  return (
    <div style={{ marginBottom: '1.25rem' }}>
      <select
        value={selectedCourse || ''}
        onChange={e => onSelect(e.target.value ? Number(e.target.value) : null)}
        style={{
          padding: '.5rem .75rem', borderRadius: '8px',
          border: '1px solid var(--border-light)',
          background: 'var(--bg-card)', color: 'inherit',
          fontSize: '.85rem', cursor: 'pointer', minWidth: '200px'
        }}
      >
        <option value="">All Courses</option>
        {courses.map(c => (
          <option key={c.endeavorId} value={c.endeavorId}>{c.name}</option>
        ))}
      </select>
    </div>
  );
}

// ─── Tutor Summary Card ─────────────────────────────────────────────

function TutorSummaryCard({ summary, loading, generating, onGenerate }) {
  const hasSummary = summary?.summaryText;

  return (
    <div style={{
      background: 'linear-gradient(135deg, #667eea22, #764ba222)',
      borderRadius: '12px', border: '1px solid var(--border-light)',
      padding: '1.25rem', marginBottom: '1.25rem'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '.75rem' }}>
        <h3 style={{ margin: 0, fontSize: '.95rem' }}>🎓 Your Study Coach</h3>
        <button
          onClick={onGenerate}
          disabled={loading || generating}
          style={{
            padding: '.3rem .6rem', fontSize: '.72rem',
            background: 'transparent', border: '1px solid var(--border-light)',
            borderRadius: '6px', cursor: generating ? 'wait' : 'pointer',
            color: 'var(--text-muted)'
          }}
        >
          {generating ? 'Generating...' : hasSummary ? '↻ Refresh' : '✨ Generate'}
        </button>
      </div>
      {loading ? (
        <p style={{ color: 'var(--text-muted)', fontSize: '.85rem', margin: 0, fontStyle: 'italic' }}>Loading...</p>
      ) : hasSummary ? (
        <>
          <p style={{ margin: '0 0 .5rem 0', fontSize: '.88rem', lineHeight: 1.5 }}>
            {summary.summaryText}
          </p>
          {summary.generatedAt && (
            <p style={{ margin: 0, fontSize: '.7rem', color: 'var(--text-muted)' }}>
              Last updated {new Date(summary.generatedAt).toLocaleDateString('en-US', {
                month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
              })}
            </p>
          )}
        </>
      ) : (
        <p style={{ color: 'var(--text-muted)', fontSize: '.85rem', margin: 0, fontStyle: 'italic' }}>
          Click "Generate" to get personalized study advice based on your progress.
        </p>
      )}
    </div>
  );
}

// ─── Next Steps / Recommendations ───────────────────────────────────

const REC_ICONS = { quiz: '🧪', flashcard_review: '🃏', read_document: '📖', chat_tutor: '💬' };

function NextSteps({ recommendations, loading, refreshing, onRefresh, onComplete, onDismiss }) {
  return (
    <div style={{
      background: 'var(--bg-card)', borderRadius: '12px',
      border: '1px solid var(--border-light)',
      padding: '1.25rem', marginBottom: '1.25rem'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '.75rem' }}>
        <h3 style={{ margin: 0, fontSize: '.95rem' }}>🎯 Next Steps</h3>
        <button
          onClick={onRefresh}
          disabled={refreshing || loading}
          style={{
            padding: '.3rem .6rem', fontSize: '.72rem',
            background: 'transparent', border: '1px solid var(--border-light)',
            borderRadius: '6px', cursor: refreshing ? 'wait' : 'pointer',
            color: 'var(--text-muted)'
          }}
        >
          {refreshing ? 'Refreshing...' : '↻ Refresh'}
        </button>
      </div>
      {loading ? (
        <p style={{ color: 'var(--text-muted)', fontSize: '.85rem', margin: 0, fontStyle: 'italic' }}>Loading recommendations...</p>
      ) : recommendations.length === 0 ? (
        <p style={{ color: 'var(--text-muted)', fontSize: '.85rem', margin: 0, fontStyle: 'italic' }}>
          No recommendations yet. Complete some quizzes or flashcard reviews to get personalized suggestions.
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '.5rem' }}>
          {recommendations.map(rec => (
            <div
              key={rec.id}
              style={{
                display: 'flex', alignItems: 'flex-start', gap: '.75rem',
                padding: '.75rem', borderRadius: '8px',
                background: 'rgba(0,123,255,.03)',
                border: '1px solid var(--border-light)'
              }}
            >
              <span style={{ fontSize: '1.3rem', lineHeight: 1 }}>{REC_ICONS[rec.recommendationType] || '📌'}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: '.85rem', marginBottom: '.2rem' }}>{rec.title}</div>
                <div style={{ fontSize: '.78rem', color: 'var(--text-muted)', lineHeight: 1.35 }}>{rec.description}</div>
              </div>
              <div style={{ display: 'flex', gap: '.3rem', flexShrink: 0 }}>
                <button
                  onClick={() => onComplete(rec.id)}
                  title="Mark complete"
                  style={recBtnStyle}
                >✓</button>
                <button
                  onClick={() => onDismiss(rec.id)}
                  title="Dismiss"
                  style={recBtnStyle}
                >✕</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Upcoming Deadlines ─────────────────────────────────────────────

function UpcomingDeadlines({ deadlines }) {
  if (!deadlines || deadlines.length === 0) return null;

  return (
    <div style={{
      background: 'var(--bg-card)', borderRadius: '12px',
      border: '1px solid var(--border-light)',
      padding: '1.25rem', marginBottom: '1.25rem'
    }}>
      <h3 style={{ margin: '0 0 .75rem 0', fontSize: '.95rem' }}>📅 Upcoming Deadlines</h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '.4rem' }}>
        {deadlines.map(d => (
          <div key={d.assignmentId} style={{
            display: 'flex', alignItems: 'center', gap: '.75rem',
            padding: '.5rem .75rem', borderRadius: '6px',
            background: d.urgent ? 'rgba(220,53,69,.06)' : 'transparent',
            border: d.urgent ? '1px solid rgba(220,53,69,.2)' : '1px solid transparent'
          }}>
            <span style={{
              fontSize: '.75rem', fontWeight: 700, minWidth: '36px', textAlign: 'center',
              padding: '.2rem .4rem', borderRadius: '4px',
              background: d.urgent ? '#dc354520' : '#007bff15',
              color: d.urgent ? '#dc3545' : '#007bff'
            }}>
              {d.daysUntil}d
            </span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: '.82rem' }}>{d.title}</div>
              {d.description && (
                <div style={{ fontSize: '.72rem', color: 'var(--text-muted)' }}>{d.description}</div>
              )}
            </div>
            <div style={{ fontSize: '.72rem', color: 'var(--text-muted)' }}>
              {new Date(d.dueDatetime).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Topic Mastery ──────────────────────────────────────────────────

function TopicMastery({ mastery, totalConcepts, loading, onTopicClick }) {
  if (loading) return null;
  if (!mastery || mastery.length === 0) {
    if (totalConcepts > 0) {
      return (
        <div style={{
          background: 'var(--bg-card)', borderRadius: '12px',
          border: '1px solid var(--border-light)',
          padding: '1.25rem', marginBottom: '1.25rem'
        }}>
          <h3 style={{ margin: '0 0 .5rem 0', fontSize: '.95rem' }}>📊 Topic Mastery</h3>
          <p style={{ color: 'var(--text-muted)', fontSize: '.82rem', margin: 0 }}>
            {totalConcepts} concept{totalConcepts > 1 ? 's' : ''} extracted. Complete quizzes or flashcard reviews to build mastery data.
          </p>
        </div>
      );
    }
    return null;
  }

  const getMasteryColor = (score) => {
    if (score >= 80) return '#28a745';
    if (score >= 50) return '#ffc107';
    return '#dc3545';
  };

  // Sort by mastery ascending (weakest first, most actionable)
  const sorted = [...mastery].sort((a, b) => a.masteryScore - b.masteryScore);

  return (
    <div style={{
      background: 'var(--bg-card)', borderRadius: '12px',
      border: '1px solid var(--border-light)',
      padding: '1.25rem', marginBottom: '1.25rem'
    }}>
      <h3 style={{ margin: '0 0 .75rem 0', fontSize: '.95rem' }}>
        📊 Topic Mastery
        <span style={{ fontSize: '.72rem', color: 'var(--text-muted)', fontWeight: 400, marginLeft: '.5rem' }}>
          {mastery.length} topic{mastery.length > 1 ? 's' : ''} tracked
        </span>
      </h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '.5rem' }}>
        {sorted.map(m => {
          const score = Number(m.masteryScore);
          const color = getMasteryColor(score);
          return (
            <div
              key={m.conceptSlug}
              onClick={() => onTopicClick(m.conceptSlug)}
              style={{
                display: 'flex', alignItems: 'center', gap: '.75rem',
                cursor: 'pointer', padding: '.4rem .5rem', borderRadius: '6px',
                transition: 'background .15s',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,123,255,.05)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <div style={{ width: '120px', fontSize: '.8rem', fontWeight: 500, flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {m.conceptSlug.replace(/-/g, ' ')}
              </div>
              <div style={{ flex: 1, height: '14px', background: 'rgba(0,0,0,.06)', borderRadius: '7px', overflow: 'hidden' }}>
                <div style={{
                  width: `${Math.max(2, score)}%`, height: '100%',
                  background: color, borderRadius: '7px',
                  transition: 'width .3s ease'
                }} />
              </div>
              <div style={{ width: '40px', textAlign: 'right', fontSize: '.8rem', fontWeight: 600, color }}>
                {score.toFixed(0)}%
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const recBtnStyle = {
  background: 'none', border: '1px solid var(--border-light)',
  borderRadius: '4px', width: '24px', height: '24px',
  cursor: 'pointer', fontSize: '.7rem', display: 'flex',
  alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)'
};

// ─── Profile Overview ───────────────────────────────────────────────

function ProfileOverview({ profile }) {
  const formatTime = (seconds) => {
    if (!seconds || seconds === 0) return '0m';
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    if (hrs > 0) return `${hrs}h ${mins}m`;
    return `${mins}m`;
  };

  const learningStyleEmoji = {
    quiz_heavy: '📝',
    chat_heavy: '💬',
    reading_heavy: '📖',
    balanced: '⚖️'
  };

  const learningStyleLabel = {
    quiz_heavy: 'Quiz Focused',
    chat_heavy: 'Chat Focused',
    reading_heavy: 'Reading Focused',
    balanced: 'Balanced'
  };

  const cards = [
    {
      label: 'Study Time',
      value: formatTime(profile.totalStudyTimeSeconds),
      icon: '⏱️',
      sub: `${profile.totalSessions || 0} sessions`
    },
    {
      label: 'Documents',
      value: profile.documentsProcessed || 0,
      icon: '📄',
      sub: 'Unique docs studied'
    },
    {
      label: 'Quizzes Done',
      value: profile.totalQuizzesCompleted || 0,
      icon: '📝',
      sub: profile.avgQuizScore != null ? `Avg score: ${Number(profile.avgQuizScore).toFixed(0)}%` : 'No scores yet'
    },
    {
      label: 'AI Prompts',
      value: profile.totalAiPrompts || 0,
      icon: '🤖',
      sub: profile.dominantPromptType ? `Mostly: ${profile.dominantPromptType}` : ''
    },
    {
      label: 'Learning Style',
      value: learningStyleLabel[profile.learningStyle] || profile.learningStyle || 'Unknown',
      icon: learningStyleEmoji[profile.learningStyle] || '📊',
      sub: ''
    },
    {
      label: 'Streak',
      value: profile.daysSinceLastActivity != null
        ? (profile.daysSinceLastActivity <= 1 ? '🔥 Active today' : `${Math.round(profile.daysSinceLastActivity)}d ago`)
        : 'N/A',
      icon: profile.daysSinceLastActivity <= 1 ? '🔥' : '📅',
      sub: profile.daysSinceLastActivity > 7 ? 'Time to study!' : ''
    }
  ];

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '.75rem', marginBottom: '1.5rem' }}>
      {cards.map((card, i) => (
        <div
          key={i}
          style={{
            padding: '1rem',
            background: 'var(--bg-card)',
            borderRadius: '10px',
            border: '1px solid var(--border-light)',
            textAlign: 'center'
          }}
        >
          <div style={{ fontSize: '1.5rem', marginBottom: '.25rem' }}>{card.icon}</div>
          <div style={{ fontSize: '1.25rem', fontWeight: 700 }}>{card.value}</div>
          <div style={{ fontSize: '.8rem', color: 'var(--text-muted)', marginTop: '.15rem' }}>{card.label}</div>
          {card.sub && (
            <div style={{ fontSize: '.7rem', color: 'var(--text-muted)', marginTop: '.25rem' }}>{card.sub}</div>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Daily Activity Chart ───────────────────────────────────────────

function DailyActivityChart({ data, days }) {
  if (!data || data.length === 0) return null;

  const chartData = data.map(d => ({
    date: new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    events: d.eventCount || 0,
    sessions: d.sessions || 0
  }));

  return (
    <div style={{ background: 'var(--bg-card)', borderRadius: '10px', border: '1px solid var(--border-light)', padding: '1.25rem', marginBottom: '1.5rem' }}>
      <h3 style={{ margin: '0 0 1rem 0', fontSize: '.95rem' }}>📈 Daily Activity — Last {days} Days</h3>
      <ResponsiveContainer width="100%" height={220}>
        <AreaChart data={chartData} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="date" tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} />
          <Tooltip />
          <Legend />
          <Area type="monotone" dataKey="events" name="Events" stroke="#007bff" fill="#007bff33" stackId="a" />
          <Area type="monotone" dataKey="sessions" name="Sessions" stroke="#28a745" fill="#28a74533" stackId="b" />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

// ─── Event Breakdown ────────────────────────────────────────────────

function EventBreakdownChart({ data }) {
  if (!data || data.length === 0) return null;

  // Top 8 events for pie chart
  const top = data.slice(0, 8).map((d, i) => ({
    name: (d.eventType || '').replace(/_/g, ' '),
    value: d.count || 0,
    color: COLORS[i % COLORS.length]
  }));

  const total = top.reduce((s, d) => s + d.value, 0);

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
      {/* Pie Chart */}
      <div style={{ background: 'var(--bg-card)', borderRadius: '10px', border: '1px solid var(--border-light)', padding: '1.25rem' }}>
        <h3 style={{ margin: '0 0 1rem 0', fontSize: '.95rem' }}>🎯 What You've Been Doing</h3>
        <ResponsiveContainer width="100%" height={200}>
          <PieChart>
            <Pie
              data={top}
              cx="50%"
              cy="50%"
              innerRadius={40}
              outerRadius={80}
              dataKey="value"
              label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
              labelLine={false}
            >
              {top.map((entry, i) => (
                <Cell key={i} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip formatter={(value) => [value.toLocaleString(), 'Events']} />
          </PieChart>
        </ResponsiveContainer>
      </div>

      {/* Breakdown List */}
      <div style={{ background: 'var(--bg-card)', borderRadius: '10px', border: '1px solid var(--border-light)', padding: '1.25rem' }}>
        <h3 style={{ margin: '0 0 .75rem 0', fontSize: '.95rem' }}>📋 Activity Breakdown</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '.4rem' }}>
          {data.slice(0, 10).map((d, i) => {
            const pct = total > 0 ? ((d.count / total) * 100).toFixed(1) : 0;
            return (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '.5rem' }}>
                <div style={{
                  width: '10px', height: '10px', borderRadius: '50%',
                  background: COLORS[i % COLORS.length], flexShrink: 0
                }} />
                <div style={{ flex: 1, fontSize: '.8rem' }}>
                  {(d.eventType || '').replace(/_/g, ' ')}
                </div>
                <div style={{ fontSize: '.8rem', fontWeight: 600 }}>{d.count}</div>
                <div style={{ fontSize: '.7rem', color: 'var(--text-muted)', width: '35px', textAlign: 'right' }}>{pct}%</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── Recent Sessions ────────────────────────────────────────────────

function RecentSessions({ sessions }) {
  const data = sessions?.sessions;
  if (!data || data.length === 0) return null;

  const formatDuration = (seconds) => {
    if (!seconds) return '—';
    const mins = Math.floor(seconds / 60);
    if (mins < 1) return `${seconds}s`;
    if (mins >= 60) return `${Math.floor(mins / 60)}h ${mins % 60}m`;
    return `${mins}m`;
  };

  return (
    <div style={{ background: 'var(--bg-card)', borderRadius: '10px', border: '1px solid var(--border-light)', padding: '1.25rem', marginBottom: '1.5rem' }}>
      <h3 style={{ margin: '0 0 1rem 0', fontSize: '.95rem' }}>📚 Recent Study Sessions</h3>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.82rem' }}>
        <thead>
          <tr style={{ borderBottom: '2px solid var(--border-light)', textAlign: 'left' }}>
            <th style={{ padding: '.5rem' }}>Date</th>
            <th style={{ padding: '.5rem', textAlign: 'right' }}>Duration</th>
            <th style={{ padding: '.5rem', textAlign: 'right' }}>Events</th>
            <th style={{ padding: '.5rem', textAlign: 'right' }}>Docs</th>
            <th style={{ padding: '.5rem', textAlign: 'right' }}>Chats</th>
            <th style={{ padding: '.5rem', textAlign: 'right' }}>Quizzes</th>
            <th style={{ padding: '.5rem', textAlign: 'right' }}>Quiz Score</th>
          </tr>
        </thead>
        <tbody>
          {data.map((s, i) => (
            <tr key={i} style={{ borderBottom: '1px solid var(--border-light)' }}>
              <td style={{ padding: '.5rem' }}>
                {s.startedAt ? new Date(s.startedAt).toLocaleDateString('en-US', {
                  month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                }) : '—'}
              </td>
              <td style={{ padding: '.5rem', textAlign: 'right' }}>{formatDuration(s.durationSeconds)}</td>
              <td style={{ padding: '.5rem', textAlign: 'right' }}>{s.eventCount || 0}</td>
              <td style={{ padding: '.5rem', textAlign: 'right' }}>{s.documentsOpened || 0}</td>
              <td style={{ padding: '.5rem', textAlign: 'right' }}>{s.chatPromptsSent || 0}</td>
              <td style={{ padding: '.5rem', textAlign: 'right' }}>{s.quizzesCompleted || 0}</td>
              <td style={{ padding: '.5rem', textAlign: 'right', fontWeight: 600, color: s.avgQuizScore >= 80 ? '#28a745' : s.avgQuizScore >= 50 ? '#ffc107' : s.avgQuizScore != null ? '#dc3545' : 'inherit' }}>
                {s.avgQuizScore != null ? `${Number(s.avgQuizScore).toFixed(0)}%` : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Session Stats Summary */}
      {sessions?.stats && (
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginTop: '1rem', padding: '.75rem', background: 'rgba(0,123,255,.05)', borderRadius: '8px' }}>
          <StatItem label="Avg Duration" value={formatDuration(sessions.stats.avgDurationSeconds)} />
          <StatItem label="Avg Events/Session" value={Number(sessions.stats.avgEventCount || 0).toFixed(1)} />
          <StatItem label="Avg Quiz Score" value={sessions.stats.avgAvgQuizScore != null ? `${Number(sessions.stats.avgAvgQuizScore).toFixed(0)}%` : 'N/A'} />
          <StatItem label="Total Sessions" value={sessions.stats.totalSessions || 0} />
        </div>
      )}
    </div>
  );
}

function StatItem({ label, value }) {
  return (
    <div style={{ textAlign: 'center', minWidth: '100px' }}>
      <div style={{ fontSize: '1.1rem', fontWeight: 700 }}>{value}</div>
      <div style={{ fontSize: '.7rem', color: 'var(--text-muted)' }}>{label}</div>
    </div>
  );
}

const btnStyle = {
  padding: '.5rem 1rem',
  background: '#007bff',
  color: '#fff',
  border: 'none',
  borderRadius: '6px',
  cursor: 'pointer'
};
